import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Upload,
  CalendarClock,
  CheckCircle2,
  CheckSquare,
  Download,
  CircleCheck,
  HardDrive,
  CheckCheck,
  XCircle,
  RefreshCw,
  Monitor,
} from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/Button';
import { SubmitRequestModal } from '@/components/requests/SubmitRequestModal';
import { useClientDashboard } from '@/api/dashboard';
import { downloadFile } from '@/api/files';
import { useAnnouncements, useDismissAnnouncement } from '@/api/announcements';
import { useChecklist } from '@/api/checklist';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores/auth';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useToast } from '@/hooks/useToast';
import { formatDistanceToNow, format } from 'date-fns';
import { apiError } from '@/lib/api';
import type { DashboardPendingRequest, FileItem } from '@/types/api';

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export function ClientDashboardPage() {
  usePageTitle('Home');
  const { user } = useAuth();
  const activeOrgId = useAuthStore((s) => s.activeOrgId);
  const orgId = activeOrgId ?? user?.organization_id ?? '';
  const { data, isLoading, isError, error } = useClientDashboard(orgId || null);
  const announcementsQuery = useAnnouncements(orgId);
  const dismissAnnouncement = useDismissAnnouncement(orgId);
  const checklist = useChecklist(orgId);
  const totalChecklist = checklist.data?.length ?? 0;
  const checkedChecklist = (checklist.data ?? []).filter((i) => i.is_checked).length;

  const [submitModal, setSubmitModal] = useState<{ id: string; targetFolderId?: string } | null>(null);

  const pendingTop = data?.pending_requests_top5 ?? [];
  const pendingCount = pendingTop.filter((r) => !isAccepted(r)).length;
  const completedCount = pendingTop.filter((r) => isAccepted(r)).length;
  const totalCount = pendingCount + completedCount;

  return (
    <AppShell crumbs={[{ label: 'Home' }]}>
      {/* Announcement banners */}
      {orgId && (announcementsQuery.data ?? []).filter((a) => a.is_active).length > 0 && (
        <div className="space-y-2 mb-4">
          {(announcementsQuery.data ?? [])
            .filter((a) => a.is_active)
            .map((ann) => (
              <div
                key={ann.id}
                className="flex items-start gap-3 bg-amber-50 border border-amber-200 px-4 py-3"
              >
                <p className="flex-1 text-sm text-amber-900">{ann.message}</p>
                <button
                  type="button"
                  onClick={() => dismissAnnouncement.mutate({ id: ann.id })}
                  disabled={dismissAnnouncement.isPending}
                  className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold text-amber-800 border border-amber-300 bg-amber-100 hover:bg-amber-200 rounded transition-colors disabled:opacity-50"
                >
                  <CheckCheck size={12} />
                  Accept
                </button>
              </div>
            ))}
        </div>
      )}

      {/* Desktop app download banner */}
      <div className="mb-4 flex items-center justify-between gap-4 rounded-lg border border-[#E3E3F1] bg-white px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#EFF0FF]">
            <Monitor size={16} className="text-[#3F4CFF]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[#12122C]">BillerBay Exchange — Desktop App</p>
            <p className="text-xs text-[#676687]">Sync files directly from Windows Explorer — no browser needed.</p>
          </div>
        </div>
        <a
          href={`${import.meta.env.VITE_API_BASE_URL ?? '/api'}/downloads/desktop`}
          className="shrink-0"
        >
          <Button variant="secondary" className="text-sm">
            <svg width={14} height={14} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M3 3h8v8H3zm10 0h8v8h-8zM3 13h8v8H3zm10 0h8v8h-8z" />
            </svg>
            Download for Windows
          </Button>
        </a>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <Spinner size={28} />
        </div>
      ) : isError ? (
        <Card>
          <div className="text-sm text-red-600">{apiError(error)}</div>
        </Card>
      ) : !data ? (
        <EmptyState title="No dashboard data" />
      ) : (
        <div className="space-y-6">
          {/* Top section: Welcome + progress */}
          <section>
            <h1 className="text-xl font-bold text-slate-800">
              Welcome back, {user?.full_name}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              You have {pendingCount} pending document request{pendingCount === 1 ? '' : 's'}.
            </p>
            {totalCount > 0 && (
              <div className="mt-3 flex items-center gap-3">
                <div className="flex-1 h-3 bg-slate-200 overflow-hidden">
                  <div
                    className="h-full bg-green-500 transition-all"
                    style={{
                      width: `${totalCount > 0 ? (completedCount / totalCount) * 100 : 0}%`,
                    }}
                  />
                </div>
                <span className="text-xs text-slate-600 whitespace-nowrap">
                  {completedCount} of {totalCount} completed
                </span>
              </div>
            )}
          </section>

          {/* Middle section: To-do cards */}
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600 mb-3">
              What BillerBay needs from you
            </h2>

            {pendingCount === 0 ? (
              <Card>
                <EmptyState
                  title="You're all caught up!"
                  description="No pending document requests."
                  icon={<CircleCheck size={24} />}
                />
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {pendingTop
                  .filter((r) => !isAccepted(r))
                  .map((req) => (
                    <TodoCard
                      key={req.id}
                      req={req}
                      onSubmit={() => setSubmitModal({ id: req.id, targetFolderId: req.target_folder_id ?? undefined })}
                    />
                  ))}
              </div>
            )}

            {/* Completed requests shown separately at bottom */}
            {completedCount > 0 && (
              <div className="mt-4">
                <h3 className="text-xs font-medium text-slate-500 mb-2">Completed</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {pendingTop
                    .filter((r) => isAccepted(r))
                    .map((req) => (
                      <div
                        key={req.id}
                        className="bg-white border border-slate-200 p-4 flex items-center gap-3"
                      >
                        <CheckCircle2 size={16} className="text-green-600 shrink-0" />
                        <Link
                          to={`/requests/${req.id}`}
                          className="text-sm text-slate-600 truncate hover:text-bb-navy"
                        >
                          {req.title}
                        </Link>
                        <Badge tone="green" className="ml-auto shrink-0">
                          Completed
                        </Badge>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </section>

          {/* Document checklist */}
          {totalChecklist > 0 && (
            <section>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600 mb-3">
                Document checklist
              </h2>
              <Card>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckSquare size={15} className="text-[#0410BD]" />
                    <span>{checkedChecklist} of {totalChecklist} items complete</span>
                  </div>
                  <div className="h-2 w-32 bg-slate-200 overflow-hidden rounded-full">
                    <div
                      className="h-full bg-[#0410BD] transition-all rounded-full"
                      style={{ width: `${totalChecklist > 0 ? (checkedChecklist / totalChecklist) * 100 : 0}%` }}
                    />
                  </div>
                </div>
                <ul className="divide-y divide-slate-100">
                  {(checklist.data ?? []).map((item) => (
                    <li key={item.id} className="flex items-center gap-3 py-2.5">
                      <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${item.is_checked ? 'bg-[#0410BD] border-[#0410BD]' : 'border-slate-300'}`}>
                        {item.is_checked && (
                          <svg viewBox="0 0 10 8" className="w-2.5 h-2.5 text-white fill-current">
                            <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                      <span className={`text-sm flex-1 ${item.is_checked ? 'line-through text-slate-400' : 'text-slate-800'}`}>
                        {item.label}
                      </span>
                      {item.is_checked && item.checked_at && (
                        <span className="text-xs text-slate-400 whitespace-nowrap">
                          {format(new Date(item.checked_at), 'PP')}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                {checkedChecklist < totalChecklist && (
                  <p className="text-xs text-slate-400 mt-3">
                    Items are checked off by your BillerBay team when documents are received and verified.
                  </p>
                )}
              </Card>
            </section>
          )}

          {/* Storage usage */}
          {(() => {
            const usedBytes = data.storage_used_bytes ?? 0;
            const quotaBytes = data.storage_quota_bytes ?? null;
            return (
              <section>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600 mb-3">
                  Storage
                </h2>
                <Card>
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-md bg-[#EFF0FF] shrink-0">
                      <HardDrive size={16} className="text-[#0410BD]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 text-sm mb-1.5">
                        <span className="font-semibold text-[#12122C]">
                          {formatBytes(usedBytes)}
                          {quotaBytes ? ` of ${formatBytes(quotaBytes)} used` : ' used'}
                        </span>
                        {quotaBytes && (
                          <span className="text-xs text-slate-500 shrink-0">
                            {Math.round((usedBytes / quotaBytes) * 100)}%
                          </span>
                        )}
                      </div>
                      {quotaBytes ? (
                        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              usedBytes / quotaBytes > 0.9
                                ? 'bg-red-500'
                                : usedBytes / quotaBytes > 0.75
                                ? 'bg-amber-500'
                                : 'bg-[#0410BD]'
                            }`}
                            style={{ width: `${Math.min(100, (usedBytes / quotaBytes) * 100)}%` }}
                          />
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400">No storage limit assigned.</p>
                      )}
                    </div>
                  </div>
                </Card>
              </section>
            );
          })()}

          {/* Bottom section: Recent documents */}
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600 mb-3">
              Recent documents
            </h2>
            <Card>
              {data.recent_files.length === 0 ? (
                <EmptyState
                  title="No documents yet"
                  description="Files shared with you will appear here."
                />
              ) : (
                <ul className="divide-y divide-slate-100">
                  {data.recent_files.slice(0, 5).map((f) => (
                    <RecentFileRow key={f.id} file={f} />
                  ))}
                </ul>
              )}
            </Card>
          </section>
        </div>
      )}

      {/* Submit request modal — shared across all to-do cards */}
      {submitModal && (
        <SubmitRequestModal
          open
          onClose={() => setSubmitModal(null)}
          requestId={submitModal.id}
          targetFolderId={submitModal.targetFolderId}
        />
      )}
    </AppShell>
  );
}

/** Check if a dashboard pending request is in an "accepted" state.
 *  The DashboardPendingRequest type has `is_overdue` — we treat
 *  not-overdue + no due_date scenario as pending, never as accepted.
 *  Accepted items are not present in pending_requests_top5 from the
 *  backend, so this is a no-op guard. In practice the backend only
 *  returns open/pending items; we include this for forward-compat. */
function isAccepted(_req: DashboardPendingRequest): boolean {
  // The backend's pending_requests_top5 only returns pending items,
  // so none are accepted. This function exists as a structural hook
  // if the backend later includes accepted items in the payload.
  return false;
}

function TodoCard({
  req,
  onSubmit,
}: {
  req: DashboardPendingRequest;
  onSubmit: () => void;
}) {
  const dueDate = req.due_date ? new Date(req.due_date) : null;
  const isRejected = req.status === 'rejected';
  const dueSoon =
    !req.is_overdue &&
    !isRejected &&
    dueDate &&
    dueDate.getTime() - Date.now() < 3 * 24 * 60 * 60 * 1000;

  return (
    <div className={`bg-white border p-4 flex flex-col gap-2 ${isRejected ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}>
      <div className="flex items-start justify-between gap-2">
        <Link
          to={`/requests/${req.id}`}
          className="text-sm font-semibold text-slate-800 truncate hover:text-bb-navy"
        >
          {req.title}
        </Link>
        {isRejected ? (
          <Badge tone="red">
            <XCircle size={12} /> Rejected
          </Badge>
        ) : req.is_overdue ? (
          <Badge tone="red">
            <AlertTriangle size={12} /> Overdue
          </Badge>
        ) : dueSoon ? (
          <Badge tone="yellow">
            <CalendarClock size={12} /> Due soon
          </Badge>
        ) : !dueDate ? (
          <Badge tone="gray">No deadline</Badge>
        ) : null}
      </div>
      {isRejected && req.review_note && (
        <div className="flex items-start gap-2 rounded-md bg-red-100 border border-red-200 px-3 py-2 text-xs text-red-800">
          <XCircle size={13} className="shrink-0 mt-0.5 text-red-500" />
          <span><strong>Reason:</strong> {req.review_note}</span>
        </div>
      )}
      {isRejected && !req.review_note && (
        <p className="text-xs text-red-700">Your submission was not accepted. Please resubmit.</p>
      )}
      {dueDate && (
        <div className="text-xs text-slate-500">
          Due {formatDistanceToNow(dueDate, { addSuffix: true })} &middot;{' '}
          {format(dueDate, 'PP')}
        </div>
      )}
      <div className="pt-1">
        <Button variant={isRejected ? 'danger' : 'secondary'} onClick={onSubmit} className="text-xs">
          {isRejected ? <><RefreshCw size={14} /> Resubmit file</> : <><Upload size={14} /> Submit file</>}
        </Button>
      </div>
    </div>
  );
}

function RecentFileRow({ file }: { file: FileItem }) {
  const toast = useToast();

  const handleDownload = async () => {
    try {
      await downloadFile(file.id, file.name);
    } catch (e) {
      toast.error('Download failed', apiError(e));
    }
  };

  return (
    <li className="py-2 flex items-center justify-between gap-3">
      <Link
        to={`/files/${file.id}`}
        className="text-sm text-slate-800 hover:text-bb-navy truncate"
      >
        {file.name}
      </Link>
      <div className="flex items-center gap-3 shrink-0">
        <span className="text-xs text-slate-500 whitespace-nowrap">
          Sent {formatDistanceToNow(new Date(file.updated_at), { addSuffix: true })}
        </span>
        <button
          onClick={handleDownload}
          className="download-button text-xs inline-flex items-center gap-1"
          title={`Download ${file.name}`}
        >
          <Download size={12} /> Download
        </button>
      </div>
    </li>
  );
}
